import React from "react";
import { Zap, Shield, Network, Boxes } from "lucide-react";

const features = [
  {
    icon: Shield,
    title: "Fairness First",
    description: "Built with zk-proofs ensuring transparent and fair execution for all participants",
    color: "#cb6ce6",
  },
  {
    icon: Network,
    title: "Community Driven",
    description: "Designed for the community with light-client-first architecture for easy access",
    color: "#e879f9",
  },
  {
    icon: Zap,
    title: "Fun & Accessible",
    description: "User-friendly experience that makes blockchain technology enjoyable and approachable",
    color: "#c084fc",
  },
  {
    icon: Boxes,
    title: "Modular Design",
    description: "Powered by Celestia DA for scalable data availability and flexible architecture",
    color: "#a855f7",
  },
];

export function FeaturesSection() {
  return (
    <section className="py-20 relative">
      <div className="text-center mb-16">
        <h2
          className="text-5xl font-bold mb-4"
          style={{
            background: "linear-gradient(135deg, #ffffff 0%, #e2e8f0 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "700",
            letterSpacing: "-0.02em",
          }}
        >
          Built for Everyone
        </h2>
        <p
          className="text-xl max-w-2xl mx-auto"
          style={{
            color: "#94a3b8",
            fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            fontWeight: "400",
          }}
        >
          A blockchain that prioritizes fairness, community participation, and enjoyable user experience
        </p>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {features.map((feature, index) => (
          <div
            key={index}
            className="group p-8 rounded-2xl transition-all duration-300 relative overflow-hidden"
            style={{
              background: "linear-gradient(135deg, rgba(51, 65, 85, 0.4) 0%, rgba(30, 41, 59, 0.6) 100%)",
              boxShadow: `
                0 4px 20px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(148, 163, 184, 0.2)
              `,
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = "translateY(-4px)";
              e.currentTarget.style.boxShadow = `
                0 12px 35px rgba(0, 0, 0, 0.3),
                inset 0 1px 0 rgba(255, 255, 255, 0.15),
                0 0 0 1px ${feature.color}40,
                0 0 40px ${feature.color}30
              `;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = "translateY(0px)";
              e.currentTarget.style.boxShadow = `
                0 4px 20px rgba(0, 0, 0, 0.2),
                inset 0 1px 0 rgba(255, 255, 255, 0.1),
                0 0 0 1px rgba(148, 163, 184, 0.2)
              `;
            }}
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-300"
              style={{
                background: `linear-gradient(135deg, ${feature.color}40 0%, transparent 100%)`,
              }}
            />

            <div className="relative z-10">
              <div
                className="p-4 rounded-xl inline-block mb-6"
                style={{
                  background: `linear-gradient(135deg, ${feature.color}20 0%, ${feature.color}10 100%)`,
                  boxShadow: `0 4px 15px ${feature.color}30`,
                }}
              >
                <feature.icon
                  className="w-8 h-8"
                  style={{
                    color: feature.color,
                    filter: `drop-shadow(0 0 10px ${feature.color}60)`,
                  }}
                />
              </div>

              <h3
                className="text-2xl font-semibold mb-3"
                style={{
                  color: "#f1f5f9",
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: "600",
                }}
              >
                {feature.title}
              </h3>

              <p
                className="text-base leading-relaxed"
                style={{
                  color: "#94a3b8",
                  fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
                  fontWeight: "400",
                  lineHeight: "1.7",
                }}
              >
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}
